import { firebaseAuth } from "@/firebase/auth";
import { type IdTokenResult, type User } from "firebase/auth";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

interface AuthContext {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<void>;
  createUserWithEmailAndPassword: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  isAuthenticated: boolean;
  hasBackendAccount: boolean;
  isAdmin: boolean;
  userId: string | null;
}

const AuthContextInternal = createContext<AuthContext | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenResult, setTokenResult] = useState<IdTokenResult | null>(null);

  useEffect(() => {
    let mounted = true;
    let tokenUnsubscribe: (() => void) | null = null;

    const initializeAuth = () => {
      try {
        // Firebase is already initialized in App.tsx, just setup auth state listener
        const unsubscribe = firebaseAuth.onAuthStateChanged(async user => {
          if (mounted) {
            setUser(user);
            setError(null);

            if (tokenUnsubscribe) {
              tokenUnsubscribe();
              tokenUnsubscribe = null;
            }

            if (user) {
              try {
                const result = await user.getIdTokenResult();
                setTokenResult(result);
                setLoading(false);

                tokenUnsubscribe = firebaseAuth.onIdTokenChanged(async user => {
                  if (user && mounted) {
                    try {
                      const result = await user.getIdTokenResult();
                      setTokenResult(result);
                    } catch (error) {
                      console.warn("Failed to get updated token result:", error);
                    }
                  }
                });
              } catch (error) {
                console.warn("Failed to get token result:", error);
                setTokenResult(null);
                setLoading(false);
              }
            } else {
              setTokenResult(null);
              setLoading(false);
            }
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error("Auth initialization failed:", error);

        if (mounted) {
          setError(error instanceof Error ? error.message : "Auth initialization failed");
          setLoading(false);
        }

        return () => {}; // return empty cleanup function
      }
    };

    const unsubscribe = initializeAuth();

    return () => {
      mounted = false;

      if (unsubscribe) {
        unsubscribe();
      }

      if (tokenUnsubscribe) {
        tokenUnsubscribe();
      }
    };
  }, []);

  const handleAuthError = (error: unknown): void => {
    const message = error instanceof Error ? error.message : "Authentication failed";
    setError(message);
    console.error("Auth error:", error);
  };

  const signInWithEmailAndPassword = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      await firebaseAuth.signInWithEmailAndPassword(email, password);
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const createUserWithEmailAndPassword = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      await firebaseAuth.createUserWithEmailAndPassword(email, password);
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setError(null);
      await firebaseAuth.signInWithGoogle();
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setError(null);
      await firebaseAuth.signOut();
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const sendPasswordResetEmail = async (email: string): Promise<void> => {
    try {
      setError(null);
      await firebaseAuth.sendPasswordResetEmail(email);
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const sendEmailVerification = async (): Promise<void> => {
    try {
      setError(null);
      await firebaseAuth.sendEmailVerification();
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const tigimClaim = tokenResult?.claims?.tigim as
    | { role?: string; user_id?: string }
    | undefined;

  const value: AuthContext = {
    user,
    loading,
    error,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithGoogle,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    isAuthenticated: !!user,
    hasBackendAccount: !!tokenResult?.claims?.tigim,
    isAdmin: tigimClaim?.role === "admin",
    userId: tigimClaim?.user_id || null
  };

  return <AuthContextInternal.Provider value={value}>{children}</AuthContextInternal.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContext => {
  const context = useContext(AuthContextInternal);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
